import { Component, inject, input, output } from '@angular/core';
import { Router } from '@angular/router';
import { UnitService } from '../../pages/units/units.service';
import { LanguageService } from '../../services/language.service';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';
import { AwareSecurityIsGodDirective, AwareSecurityIsGrantedDirective, AwareSecurityService } from '@appbolaget/aware-security';
import { Store } from '@ngxs/store';
import { UnitState } from 'app/state/unit.state';
import { Unit } from '@appbolaget/aware-model';
import { Client } from '@appbolaget/aware-model';
import { SetActiveUnit } from 'app/state/unit.actions';
import { Attribute } from '@appbolaget/aware-model';
import { ApiEnv, AwareHttpService } from '@appbolaget/aware-http';
import { SetApiEnv } from 'app/state/app.actions';
import { AppState } from 'app/state/app.state';
import { NotificationState } from '@modules/notification/notification.state';
import { AwareAuthService, AwareAuthState } from '@appbolaget/aware-auth';
import { MessagesService } from '@pages/messages/messages.service';
import { MESSAGES_ROUTE } from '@pages/messages/symbols';
import { TranslateModule } from '@ngx-translate/core';
import { NgArrayPipesModule, NgStringPipesModule } from 'ngx-pipes';
import { MatDividerModule } from '@angular/material/divider';
import { MatBadgeModule } from '@angular/material/badge';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { AsyncPipe } from '@angular/common';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TopbarUnitTreeComponent } from './topbar-unit-tree.component';
import { TopbarService } from './topbar.service';
import { TopbarFeature } from './TopbarFeature';
import { Config, VersionService } from '@services';
import { ClientAvatarComponent } from '@components/client-avatar/client-avatar.component';

@Component({
    selector: 'app-topbar',
    templateUrl: './topbar.component.html',
    styleUrls: ['./topbar.scss'],
    providers: [UnitService],
    imports: [
        MatToolbarModule,
        MatButtonModule,
        MatIconModule,
        AwareSecurityIsGodDirective,
        MatTooltipModule,
        MatMenuModule,
        MatBadgeModule,
        MatDividerModule,
        AsyncPipe,
        NgArrayPipesModule,
        NgStringPipesModule,
        TranslateModule,
        TopbarUnitTreeComponent,
        AwareSecurityIsGrantedDirective,
        ClientAvatarComponent,
    ],
})
export class TopbarComponent {
    #store = inject(Store);
    #awareAuth = inject(AwareAuthService);
    #ass = inject(AwareSecurityService);
    #config = inject(Config);
    #messagesService = inject(MessagesService);
    #topbarService = inject(TopbarService);
    api = inject(AwareHttpService);
    unitService = inject(UnitService);
    versionService = inject(VersionService);
    router = inject(Router);
    languageService = inject(LanguageService);
    availableEnvs$ = this.#store.select(AppState.availableEnvs);
    activeUnit$ = this.#store.select(UnitState.activeUnit);
    client$ = this.#awareAuth.clientFiltered$;
    hijacker$ = this.#awareAuth.state$.pipe(map((state: AwareAuthState) => state.hijacker));

    systemNotices$ = this.#store.select(NotificationState.notificationsByType('systemnotice'));

    // units: Array<Unit>;
    hasSidebar$: Observable<boolean> = this.#ass.isGranted('sidemenu.read');

    readonly isMobile = input<boolean>(false);
    readonly toggleSidenav = output<void>();

    messagesRoute = MESSAGES_ROUTE;
    unreadThreads$ = this.#messagesService.unreadThreads$;

    topbarFeatures$ = this.#topbarService.currentFeatures$;
    applicableFeatures$ = this.#topbarService.mergedApplicableFeatures$;
    mergedBadgeSourcesForHiddenFeatures$ = this.#topbarService.mergedBadgeSourcesForHiddenFeatures$;
    units$: Observable<Unit[]> = this.#awareAuth.clientFiltered$.pipe(
        map((client) => (client instanceof Client && client.units ? client.units : [])),
        map((units) => this._parseUnits(units)),
    );
    hasApiLanguageEnabled$ = this.activeUnit$.pipe(map(() => !!this.#config.get('unitConfig.panel.enableApiLanguageMenu')));
    hasPanelLanguageEnabled$ = this.activeUnit$.pipe(map(() => !!this.#config.get('unitConfig.panel.enableLanguageMenu')));

    featureClicked(feature: TopbarFeature, event: MouseEvent) {
        this.#topbarService.featureClicked(feature, event);
    }

    togglePinnedFeature(feature: TopbarFeature) {
        this.#topbarService.togglePinnedFeature$.next(feature);
    }

    setApiEnv(env: ApiEnv) {
        this.#store.dispatch(new SetApiEnv(env));
    }

    setApiLanguage(language) {
        this.languageService.setApiLanguage(language.code);
    }

    setLanguage(language: any) {
        this.languageService.changeLanguage(language.code);
    }

    goTo(route: string) {
        this.router.navigateByUrl(route);
    }

    goToSettings() {
        this.router.navigateByUrl('app/me/settings');
    }

    async cancelHijack() {
        await this.#awareAuth.cancelHijack();
        this.router.navigate(['/app', 'dashboard']);
    }

    goToUnit(unit: Unit) {
        this.router.navigate([`/app/units/${unit.uuid}`]);
    }

    refreshToken() {
        this.#awareAuth.refreshToken();
    }

    async setActiveUnit(unit: Unit) {
        this.#store.dispatch(new SetActiveUnit(unit));

        const nonRedirectRoutes = [
            'app/posts',
            'app/calendar',
            'app/units',
            'app/clients',
            'app/dashboard',
            'app/statistics',
            'app/week-scheduler',
        ];

        const shouldStay = nonRedirectRoutes.some((route) => this.router.url.includes(route));

        if (!shouldStay) {
            this.router.navigateByUrl(`app/units/${unit.uuid}/information`);
        }
    }

    signout() {
        this.#awareAuth.logout();
    }

    toggleSideNav() {
        this.toggleSidenav.emit();
    }

    private _getParentArray(unit: Unit, parentArray = []) {
        if (!unit?.parent) {
            return parentArray;
        }

        parentArray.push(new Unit(unit.parent));

        return this._getParentArray(unit.parent, parentArray);
    }

    private _parseUnits(units: Unit[]) {
        const data: any = [];

        for (const unit of units) {
            this.unitService._pushUnits(unit.children, data);

            const parentArray = this._getParentArray(unit).reverse();

            if (!unit.attributes?.length) {
                unit.attributes = [];
            }

            unit.attributes.push(new Attribute({ key: 'parents', json: parentArray }));
            // unit.parents = parentArray;
        }

        /*
            Code for also including children to the units.
         */
        // for (const unit of data) {
        //     const exists = units.filter(obj => obj.uuid === unit.uuid).length > 0;

        //     if (!exists) {
        //         const parentArray = this._getParentArray(unit).reverse();

        //         unit.parents = parentArray;

        //         units.push(unit);
        //     }
        // }

        return units;
    }
}
